package gr.tech.site.styles

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focus
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.*

// Header & Description

/** Main H2 at the top of the Contact Us page */
val ContactUsHeader = CssStyle {
    base {
        Modifier
            .color(Theme.DarkGreen.rgb)
            .fontWeight(FontWeight.Bold)
            .letterSpacing((-2).px)
            .fontSize(2.5.cssRem)
            .textAlign(TextAlign.Start)
            .margin(top = 1.5.cssRem, bottom = .5.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .textAlign(TextAlign.Center)
            .fontSize(3.cssRem)
            .margin(top = 3.cssRem, bottom = .5.cssRem)
    }
}

/** Muted lead paragraph beneath the Contact Us H2 */
val ContactUsDescription = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .color(Theme.DarkGreen.rgb)
            .opacity(75.percent)
            .margin(bottom = 1.5.cssRem)
            .textAlign(TextAlign.Start)
    }
    Breakpoint.MD {
        Modifier
            .textAlign(TextAlign.Center)
            .margin(bottom = 2.5.cssRem)
    }
}

// Right Panel (Info Column)

val ContactInfoDividerStyle = CssStyle {
    base {
        Modifier
            .borderLeft(0.px, LineStyle.Solid,rgba(0,0,0,0.25) )
    }
    Breakpoint.MD {
        Modifier
            .borderLeft(1.px, LineStyle.Solid,rgba(0,0,0,0.25) )
    }
}

/** "Chat With Us" / "Call Us" section headings in the info panel */
val ContactInfoTitleStyle = CssStyle.base {
    Modifier
        .color(Theme.DarkGreen.rgb)
        .fontWeight(FontWeight.Bold)
        .fontSize(1.cssRem)
        .margin(bottom = 2.px)
}

/** Small muted sub-text beneath each info section title */
val ContactInfoSubtextStyle = CssStyle.base {
    Modifier
        .fontSize(0.875.cssRem)
        .color(rgba(0, 0, 0, 0.5))
        .margin(bottom = 24.px)
}

/**
 * Applied to the text portion of an email/phone link item.
 * Dark green, bold, dark green border-bottom underline.
 * Icon siblings should NOT receive this style.
 */
val ContactInfoLinkTextStyle = CssStyle.base {
    Modifier
        .color(Theme.DarkGreen.rgb)
        .fontSize(1.cssRem)
        .borderBottom(1.px, LineStyle.Solid, Theme.DarkGreen.rgb)
        .lineHeight(1)
}

/** Dark green color for icons inside the info panel (no underline treatment) */
val ContactInfoIconStyle = CssStyle.base {
    Modifier.color(Theme.DarkGreen.rgb)
}

// Form Input Labels

val FormInputLabelStyle = CssStyle.base {
    Modifier
        .fontSize(0.8.cssRem)
        .color(rgba(0, 0, 0, 0.65))
}

// Form Section Headers

/** H3 separators inside the Salesforce form ("Tell us about your Project" etc.) */
val ContactUsSectionHeader = CssStyle {
    base {
        Modifier
            .color(Theme.DarkGreen.rgb)
            .fontSize(1.5.cssRem)
            .margin(top = 1.5.cssRem, bottom = 0.25.cssRem)
    }
}

val ContactUsSectionSubtitle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(bottom = 1.cssRem)
            .color(Theme.DarkGreen.rgb)
            .fontSize(1.cssRem)
            .opacity(0.75)
            .opacity(75.percent)
            .textAlign(TextAlign.Start)
    }
}

// QuForm Inputs

val RoomUseTutorialStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.8.cssRem)
            .display(DisplayStyle.None)
    }
    Breakpoint.MD {
        Modifier
            .display(DisplayStyle.Block)
    }
}

fun Modifier.noBorder(): Modifier {
    return this
        .border(
            width = 0.px,
            style = LineStyle.None,
            color = Colors.Transparent
        )
        .outline(
            width = 0.px,
            style = LineStyle.None,
            color = Colors.Transparent
        )
}

fun baseInputModifier() = Modifier
    .fontSize(0.8.cssRem)
    .width(100.percent)
    .minWidth(100.percent)
    .height(34.px)
    .borderRadius(4.px)
    .padding(4.px)
    .color(Colors.Black)
    .backgroundColor(Colors.White)
    .appearance(Appearance.None)
    .styleModifier {
        property("-webkit-appearance", "none")
    }
    .boxSizing(BoxSizing.BorderBox)
    .border(
        width = 1.px,
        style = LineStyle.Solid,
        color = Colors.LightGray
    )
    .outline(
        width = 0.px,
        style = LineStyle.None,
        color = Colors.Transparent
    )
    .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 0.px, spreadRadius = 0.px, color = Theme.MediumGreen.rgb)
    .transition(Transition.all(250.ms))

val ContactInputStyle = CssStyle {
    base {
        baseInputModifier()
            .width(100.percent)
    }
    focus {
        Modifier
            .border(
                width = 1.px,
                style = LineStyle.Solid,
                color = Theme.MediumGreen.rgb
            )
            .boxShadow(offsetX = 0.px, offsetY = 0.px, blurRadius = 0.px, spreadRadius = 4.px, color = Theme.MediumGreen.rgb)
    }
}

val roomUseResponsiveStyle = CssStyle {
    base {
        Modifier
            .padding(2.px)
            .height(110.px)

    }
    Breakpoint.MD {
        Modifier
    }
}

val InputStyleHalf = CssStyle {
    base {
        baseInputModifier()
    }
    Breakpoint.MD {
        baseInputModifier()
            .fillMaxWidth(50.percent)
    }
    focus {
        Modifier
            .border(
                width = 2.px,
                style = LineStyle.Solid,
                color = Theme.DarkGreen.rgb
            )
    }
}

val InputColumn = CssStyle {
    base {
        Modifier
            .padding(leftRight = 0.px)
            .fillMaxWidth()
    }
    Breakpoint.MD {
        Modifier
//            .padding(leftRight = 16.px)
    }
}

val InputColumnHalf = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
    }
    Breakpoint.MD {
        Modifier
            .fillMaxWidth(50.percent)
    }
}

val ContactRow = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = 8.px)
            .gap(32.px)
    }
    Breakpoint.MD {
        Modifier
            .padding(topBottom = 16.px)
    }
}

val ResponsiveColumns = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
    }
    Breakpoint.MD {
        Modifier
            .flexDirection(FlexDirection.Row)
    }
}

val SubmitButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Theme.DarkGreen.rgb)
            .noBorder()
            .borderRadius(5.px)
            .padding(topBottom = 16.px, leftRight = 16.px)
            .color(Colors.White)
            .fontSize(1.15.cssRem)
            .cursor(Cursor.Pointer)
    }
    hover {
        Modifier
            .transition(Transition.all(250.ms))
            .scale(.96)
    }
}
